/**
 * PAGE STATS (KPR-543): how long the current page took, split into network,
 * server, client and other, worked out in the browser from its own Resource
 * Timing entries and the API's `Server-Timing` header (keepr-api KPR-542). A
 * pure function: nothing is sent or stored.
 *
 * The view runs from `viewStart` (0 on a first load, the route change after)
 * to `end` (the last API response, or the paint that followed it). Every
 * millisecond of it is billed to exactly one bucket, so the four add up to the
 * total:
 *   - other:   before the first API request left (the document and the app's
 *              files on a first load);
 *   - server:  the API's own time, the union of the `total` each response
 *              reports, placed just before its response started;
 *   - network: the rest of the time a request was in flight;
 *   - client:  the rest (rendering between and after the requests).
 * `server` is null when no response carried `Server-Timing` (an older API); the
 * wait is then inside `network`.
 */

const round = n => Math.round(n * 10) / 10;

/** Total length of a set of [start, end] intervals, overlaps counted once. */
export function unionMs(intervals) {
	const sorted = intervals.filter(([a, b]) => b > a).sort((x, y) => x[0] - y[0]);
	let total = 0;
	let curStart = null;
	let curEnd = null;
	for (const [a, b] of sorted) {
		if (curEnd === null || a > curEnd) {
			if (curEnd !== null) total += curEnd - curStart;
			curStart = a;
			curEnd = b;
		} else if (b > curEnd) curEnd = b;
	}
	if (curEnd !== null) total += curEnd - curStart;
	return total;
}

/** Intersection of two interval lists, as a list. */
function clipTo(intervals, bounds) {
	const out = [];
	for (const [a, b] of intervals) {
		for (const [c, d] of bounds) {
			const lo = Math.max(a, c);
			const hi = Math.min(b, d);
			if (hi > lo) out.push([lo, hi]);
		}
	}
	return out;
}

const serverTotalOf = entry => {
	const list = entry && entry.serverTiming;
	if (!list || typeof list.length !== 'number') return null;
	for (let i = 0; i < list.length; i += 1) {
		if (list[i] && list[i].name === 'total' && Number.isFinite(list[i].duration)) return list[i].duration;
	}
	return null;
};

/**
 * @param {object} input
 * @param {Array} input.entries  Resource Timing entries (any origin, filtered here).
 * @param {string} input.apiUrl  The API's URL prefix (`API_URL`).
 * @param {number} [input.viewStart=0]  ms on the performance clock.
 * @param {number} [input.paintedAt=0]  ms the last data reached the screen.
 * @returns {null | { total, network, server, client, other, requests, serverKnown }}
 *   null when no API request belongs to this view yet.
 */
export function computePageStats({ entries = [], apiUrl = '/api', viewStart = 0, paintedAt = 0 } = {}) {
	let prefix = apiUrl;
	try {
		prefix = new URL(apiUrl, typeof window !== 'undefined' && window.location ? window.location.href : undefined).href;
	} catch { /* keep the raw prefix */ }
	const calls = entries.filter(e => e
		&& typeof e.name === 'string'
		&& e.name.startsWith(prefix)
		&& e.startTime >= viewStart
		&& e.responseEnd > e.startTime);
	if (!calls.length) return null;

	const firstStart = Math.min(...calls.map(e => e.startTime));
	const lastEnd = Math.max(...calls.map(e => e.responseEnd));
	const end = Math.max(lastEnd, paintedAt > viewStart ? paintedAt : 0);
	const total = end - viewStart;

	const flight = calls.map(e => [e.startTime, e.responseEnd]);
	const flightMs = unionMs(flight);

	const known = calls.map(e => [e, serverTotalOf(e)]).filter(([, s]) => s !== null);
	const serverKnown = known.length > 0;
	// The API's work ends where the response begins: place it there.
	const serverIntervals = clipTo(
		known.map(([e, s]) => {
			const stop = e.responseStart > e.startTime ? e.responseStart : e.responseEnd;
			return [Math.max(e.startTime, stop - s), stop];
		}),
		flight.length ? [[firstStart, lastEnd]] : []
	);
	const serverMs = Math.min(unionMs(serverIntervals), flightMs);
	const networkMs = flightMs - serverMs;
	const otherMs = Math.max(0, firstStart - viewStart);
	const clientMs = Math.max(0, total - flightMs - otherMs);

	return {
		total: round(total),
		network: round(networkMs),
		server: serverKnown ? round(serverMs) : null,
		client: round(clientMs),
		other: round(otherMs),
		requests: calls.length,
		serverKnown,
	};
}
